import type { Dayjs } from '@atlas/tool/box/date/dayjs';
import dayjs, { setDayjsLocale, withActiveLocale } from '@atlas/tool/box/date/dayjs';

export const isCurrentYear = (date: Dayjs) => date.isSame(dayjs(), 'year');

export const formatWithAlternativeYear = (date: Dayjs, formatWithoutYear: string, formatWithYear: string) => {
    const isThisYear = isCurrentYear(date);

    return date.format(isThisYear ? formatWithoutYear : formatWithYear);
};

export const parseDateUTC = (iso: string): Dayjs => {
    // "Z" indicates UTC time, otherwise either dayjs(...) or new Date(...) parse it as local time
    return dayjs(typeof iso === 'string' && iso.endsWith('Z') ? iso : iso + 'Z');
};

export const parseDate = (iso: string): Dayjs => {
    return parseDateUTC(iso).tz();
};

export const shortTextDate = function (date: Dayjs) {
    if (!date) {
        return '';
    }
    return withActiveLocale(date).format('ll');
};

export const shortDateTimeDisplay = function (
    date: Dayjs | string,
    includeWeekday = false,
    separator = ', ',
    shortWeekday = false,
) {
    if (!date) {
        return '';
    }

    date = dayjs(date);

    if (dayjs().tz().isSame(date, 'day')) {
        return withActiveLocale(date).format('LT');
    }

    const year = isCurrentYear(date) ? '' : ', YYYY';

    const DATE_PART_FORMAT = `${includeWeekday ? (shortWeekday ? 'ddd' : 'dddd') + separator : ''}MMM D${year}`;

    return withActiveLocale(date).format(DATE_PART_FORMAT) + separator + withActiveLocale(date).format('LT');
};

export const shortDateDisplay = function (date: Dayjs) {
    if (!date) {
        return '';
    }

    if (dayjs().tz().isSame(dayjs(date).tz(), 'day')) {
        return withActiveLocale(date).format('LT');
    }

    return formatWithAlternativeYear(date, 'MMM D', 'll');
};

export function shortDateTimeIntervalDisplay(start?: Dayjs, end?: Dayjs, separator = ' - ') {
    if (!start || !end) {
        return '';
    }

    const startDisplay = withActiveLocale(start).format('l LT');
    let endDisplay: string;

    if (start.isSame(end, 'day')) {
        endDisplay = withActiveLocale(end).format('LT');
    } else {
        endDisplay = withActiveLocale(end).format('l LT');
    }

    return startDisplay + separator + endDisplay;
}

const formatDateOrTime = (date: Dayjs, type: 'date' | 'time') =>
    type === 'date' ? (isCurrentYear(date) ? 'MMM D' : 'll') : 'LTS';

export function longIntervalDisplay(start?: Dayjs, end?: Dayjs, type: 'date' | 'time' = 'date', separator = ' – ') {
    if (!start || !end) {
        return '';
    }

    const startDisplay = withActiveLocale(start).format(formatDateOrTime(start, type));
    let endDisplay = '';

    if (type === 'time' || !start.isSame(end, 'day')) {
        endDisplay = withActiveLocale(end).format(formatDateOrTime(end, type));
    }

    if (!endDisplay) {
        return startDisplay;
    }

    return `${startDisplay}${separator}${endDisplay}`;
}

export const humanizeTime = function (date: Dayjs | string) {
    const parsed = typeof date === 'string' ? parseDate(date) : date;
    return withActiveLocale(parsed).fromNow();
};

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect, afterEach, beforeEach } = import.meta.vitest;

    describe('time-ts', () => {
        const may6 = () => dayjs('2024-05-06T10:30:00');
        const may6Later = () => dayjs('2024-05-06T11:45:00');

        beforeEach(() => setDayjsLocale('en'));
        afterEach(() => setDayjsLocale('en'));

        it('prints short dates in the reader language', () => {
            expect(shortTextDate(may6())).toBe('May 6, 2024');

            setDayjsLocale('de');
            expect(shortTextDate(may6())).toBe('6. Mai 2024');
        });

        it('drops the date for today and the year for this year', () => {
            const today = dayjs().hour(10).minute(30);

            expect(shortDateTimeDisplay(today)).toBe('10:30 AM');
            // Earlier this year: the date appears, the year does not.
            const threeDaysAgo = dayjs().subtract(3, 'day').hour(10).minute(30);
            expect(shortDateTimeDisplay(threeDaysAgo)).toBe(`${threeDaysAgo.format('MMM D')}, 10:30 AM`);
            expect(shortDateDisplay(may6())).toBe('May 6, 2024');

            setDayjsLocale('de');
            expect(shortDateTimeDisplay(today)).toBe('10:30');
        });

        it('localizes interval labels', () => {
            expect(shortDateTimeIntervalDisplay(may6(), may6Later())).toBe('5/6/2024 10:30 AM - 11:45 AM');

            setDayjsLocale('de');
            expect(shortDateTimeIntervalDisplay(may6(), may6Later())).toBe('6.5.2024 10:30 - 11:45');
            expect(longIntervalDisplay(may6(), may6Later(), 'time', ' - ')).toBe('10:30:00 - 11:45:00');
        });

        it('humanizes elapsed time in the reader language', () => {
            const twoHoursAgo = dayjs().subtract(2, 'hour');

            expect(humanizeTime(twoHoursAgo)).toBe('2 hours ago');

            setDayjsLocale('fr');
            expect(humanizeTime(twoHoursAgo)).toBe('il y a 2 heures');
        });
    });
}

// #endregion
